'use client';

import { useEffect, useMemo, useState } from 'react';
import { BaseTranslations } from '../../../../baseTranslations';
import { CmsProduct } from '../../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { filterActiveOffers } from '../../../../utils/helpers/offers';
import { LoanSearchParams } from '../../../../utils/searchParams/loanSearchParams';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../utils/translations/tr';
import { MaterialIcon } from '../../../MaterialIcon';
import { ProductDetailsSectionHeader } from '../../BaseComponents/ProductDetailsSectionHeader';
import { ProductDetailsSectionWrapper } from '../../BaseComponents/ProductDetailsSectionWrapper';
import { ProductDetailsSectionOffersPlAsideModal } from './components/ProductDetailsSectionOffersPlAsideModal/ProductDetailsSectionOffersPlAsideModal';
import { useOpenProductDetailsSectionOffersPlAsideModal } from './components/ProductDetailsSectionOffersPlAsideModal/useOpenProductDetailsSectionOffersPlAsideModal';
import { useProductDetailsSectionOffersPlAsideModal } from './components/ProductDetailsSectionOffersPlAsideModal/useProductDetailsSectionOffersPlAsideModal';
import { ProductDetailsSectionOffersPlSummaryCard } from './components/ProductDetailsSectionOffersPlSummaryCard/ProductDetailsSectionOffersPlSummaryCard';
import { useProductDetailsSectionOffersPlSummaryCard } from './components/ProductDetailsSectionOffersPlSummaryCard/useProductDetailsSectionOffersPlSummaryCard';
import { ProductDetailsSectionOffersPlTable } from './components/ProductDetailsSectionOffersPlTable/ProductDetailsSectionOffersPlTable';
import { useProductDetailsSectionOffersPlTable } from './components/ProductDetailsSectionOffersPlTable/useProductDetailsSectionOffersPlTable/useProductDetailsSectionOffersPlTable';
import { getOffersPlNotes } from './getOffersPlNotes';
import { TRANSLATIONS } from './i18n';

type ProductDetailsSectionOffersPlProps = {
  product?: CmsProduct;
  pushDataLayer?: PushDataLayerFn;
  /** The page's loan selection; the section reads it and writes it back. */
  loanSearchParams?: LoanSearchParams;
  onLoanSearchParamsChange?: (params: LoanSearchParams) => void;
};

// PL Offers section (CMS-2023): summary card, amount-by-tenure reward matrix
// and the offer details panel. Replaces ProductDetailsSectionGift for PL.
export const ProductDetailsSectionOffersPl: React.FC<
  ProductDetailsSectionOffersPlProps
> = ({ product, loanSearchParams, onLoanSearchParamsChange }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const text = getClientTranslations(TRANSLATIONS, context);

  // Offer periods are checked after mount so SSR and hydration agree.
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => setNow(new Date()), []);

  const offers = useMemo(() => {
    // Bundle offers are Exclusive Offer entries this product is bundled into,
    // so they count as Exclusive offers here (same as ProductDetailsSectionGift
    // and the Offers visibility rule), although the ticket names only the
    // Exclusive and Welcome types.
    const allOffers = [
      ...(product?.exclusive_offers ?? []),
      ...(product?.bundle_exclusive_offers ?? []),
      ...(product?.welcome_offers ?? []),
    ];
    return now ? filterActiveOffers(allOffers, now) : allOffers;
  }, [product, now]);

  const summaryCard = useProductDetailsSectionOffersPlSummaryCard({
    productName: product?.product_name,
    offers,
    loanSearchParams,
  });
  const table = useProductDetailsSectionOffersPlTable({
    offers,
    loanSearchParams,
    onLoanSearchParamsChange,
  });

  const panelEntries = useProductDetailsSectionOffersPlAsideModal({
    offers,
    loanSearchParams,
  });
  const panel = useOpenProductDetailsSectionOffersPlAsideModal(product?.uid);
  const notes = getOffersPlNotes(product);

  return (
    <div className="flex flex-col gap-spacing/gap/y">
      <ProductDetailsSectionHeader
        icon={<MaterialIcon icon="diamond" size={3} />}
      >
        {tr('pd_nav_offers', staticText)}
      </ProductDetailsSectionHeader>
      <ProductDetailsSectionOffersPlSummaryCard
        {...summaryCard}
        onSeeMore={panel.open}
      />
      {table && (
        <ProductDetailsSectionWrapper>
          <div className="flex flex-col gap-spacing/gap/y p-spacing/padding/x">
            <h3 className="m-0 mhg-alpha-typography-subheading-2 text-text/primary">
              {tr('matrix_heading', text)}
            </h3>
            <ProductDetailsSectionOffersPlTable
              {...table}
              onOpenDetails={panel.open}
            />
          </div>
        </ProductDetailsSectionWrapper>
      )}
      {notes.map((note) => (
        <p
          key={note}
          className="m-0 mhg-alpha-typography-body-2-relaxed text-text/primary"
        >
          {note}
        </p>
      ))}
      <ProductDetailsSectionOffersPlAsideModal
        isOpen={panel.isOpen}
        onClose={panel.close}
        entries={panelEntries}
      />
    </div>
  );
};
